import { mapValues } from 'es-toolkit';
import { createSelector } from '@reduxjs/toolkit';

import { calculateBearingSegment, calculateMemberPolygon } from '@paragon/three-shared';
import { max, min } from '@paragon/utilities';

import { RootState } from '../index';
import { getBearingVisualData } from './bearingGeometrySelectors';

export interface ComponentSpans {
  bearingSpan: number;
  bottomChordLength: number;
  overallLength: number;
}

function getWidth(xCoordinates: number[]): number {
  return max(xCoordinates)! - min(xCoordinates)!;
}

export const getComponentSpans = createSelector(
  (state: RootState) => state.componentDesigns.data,
  (componentDesignsByGuid): { [componentDesignGuid: string]: ComponentSpans | null } =>
    mapValues(componentDesignsByGuid, (componentDesign) => {
      if (!componentDesign) return null;

      const bearingXCoordinates: number[] = [];
      const componentXCoordinates: number[] = [];
      const bottomChordXCoordinates: number[] = [];

      for (const bearing of componentDesign.component.bearings) {
        const bearingData = getBearingVisualData(componentDesign.component, bearing);

        if (!bearingData) {
          console.error("Unable to calculate a bearing's position, so some component widths may be incorrect.");
          continue;
        }

        const bearingSegment = calculateBearingSegment(bearingData);
        bearingXCoordinates.push(bearingSegment.basePoint.x);
        bearingXCoordinates.push(bearingSegment.endPoint.x);
      }

      for (const member of componentDesign.component.members) {
        const isBottomChord = member.memberType === 'BottomChord';
        const polygon = calculateMemberPolygon(member, componentDesign.component);

        if (!polygon) {
          console.error('Member polygon is null, so some component widths may be incorrect.');
          continue;
        }

        for (const point of polygon.vertices) {
          componentXCoordinates.push(point.x);

          if (isBottomChord) {
            bottomChordXCoordinates.push(point.x);
          }
        }
      }

      return {
        bearingSpan: bearingXCoordinates.length > 1 ? getWidth(bearingXCoordinates) : 0,
        bottomChordLength: bottomChordXCoordinates.length > 1 ? getWidth(bottomChordXCoordinates) : 0,
        overallLength: componentXCoordinates.length > 1 ? getWidth(componentXCoordinates) : 0,
      };
    }),
);
